import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const RULES = [
  { id: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { id: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "num", label: "A number", test: (p) => /\d/.test(p) },
];

export default function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const valid = RULES.every((r) => r.test(next)) && next === confirm && current;

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use the new password next time you log in.");
      reset();
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const type = show ? "text" : "password";
  const cls = "mt-1.5 bg-slate-950 border-slate-700 text-white";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button data-testid="admin-change-password-btn" variant="outline" className="border-slate-700 text-slate-200" onClick={() => setOpen(true)}>
        <KeyRound className="w-4 h-4 mr-1" /> Change password
      </Button>
      <DialogContent data-testid="change-password-modal" className="max-w-md bg-slate-950 border-slate-800 text-white">
        <DialogHeader>
          <DialogTitle className="display uppercase">Change admin password</DialogTitle>
          <DialogDescription className="text-slate-400">Pick something only you know. You'll stay signed in on this device.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Current password</Label>
            <Input data-testid="change-password-current" type={type} value={current} onChange={(e) => setCurrent(e.target.value)} className={cls} autoComplete="current-password" required />
          </div>
          <div>
            <Label>New password</Label>
            <Input data-testid="change-password-new" type={type} value={next} onChange={(e) => setNext(e.target.value)} className={cls} autoComplete="new-password" required />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input data-testid="change-password-confirm" type={type} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={cls} autoComplete="new-password" required />
            {confirm && next !== confirm && <div data-testid="change-password-mismatch" className="text-xs text-rose-400 mt-1">Passwords don't match</div>}
          </div>
          <ul className="grid grid-cols-2 gap-1.5 text-xs">
            {RULES.map((r) => (
              <li key={r.id} data-testid={`change-password-rule-${r.id}`} className={r.test(next) ? "text-emerald-400" : "text-slate-500"}>{r.test(next) ? "✓" : "○"} {r.label}</li>
            ))}
          </ul>
          <div className="flex items-center justify-between pt-2">
            <button type="button" data-testid="change-password-toggle-show" onClick={() => setShow((s) => !s)} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1">
              {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? "Hide" : "Show"} passwords
            </button>
            <Button data-testid="change-password-submit" type="submit" disabled={!valid || busy} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Update password
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
